import { buildingVisibility, visibilityAtLevel } from './buildingVisibility.js';
import { ptInPoly, distToSeg, blocked } from './geometry.js';
import { cameraAppearance } from './cameraAppearance.js';
import { cameraMountKind, isCeilingCamera, isCeilingKind, ceilingClearance, pendantDrop, mountingProfile } from './cameraMounts.js';

const surfaceKey = (w, id = w.sourceId) => JSON.stringify([w.sourceFloorId, w.objectKind, id]);
// Shared ceiling/slab meshes retain the identity and footprint of each room.
// Combining coplanar geometry must not detach saved mounts or let a camera
// remain attached to a room after its supporting area has been removed.
function supportKey(w, camera, keepSurface) {
  const source = (w.sources || [{ id: w.sourceId, pts: w.roofPts }]).find(s =>
    (!keepSurface || !camera.mountSurface || surfaceKey(w, s.id) === camera.mountSurface) && ptInPoly(camera.x, camera.y, s.pts));
  return source ? surfaceKey(w, source.id) : null;
}
function contains(w, x, y, radius) {
  if (!ptInPoly(x, y, w.roofPts) || w.roofHoles?.some(r => ptInPoly(x, y, r))) return false;
  return [w.roofPts, ...(w.roofHoles || [])].every(r => r.every((p, i) => {
    const q = r[(i + 1) % r.length]; return distToSeg(x, y, p.x, p.y, q.x, q.y) >= radius;
  }));
}

/** A ceiling is the underside of a modeled roof or slab. Open sky, furniture
 * and holes through a floor cannot support a ceiling bracket. */
export function ceilingCameraPose(S, camera, model = S.models.find(m => m.id === camera.modelId), { keepSurface = false, context, lensHeight } = {}) {
  const mountKind = cameraMountKind(model, camera);
  if (!isCeilingKind(mountKind) || !mountingProfile(model).kinds.includes(mountKind)) return null;
  const walls = S._mountWalls || (S._sceneWalls?.some(w => w.sourceFloorId) ? S._sceneWalls : null) || visibilityAtLevel(context || buildingVisibility(S), S.elevation || 0).walls;
  const clearance = ceilingClearance(model);
  let mountDrop = pendantDrop({ ...camera, mountKind });
  const radius = 0.08 * cameraAppearance(model).scale;
  const surfaces = walls.filter(w => (w.objectKind === 'slab' || w.objectKind === 'roof' && w.sourceFloorId === S.activeFloorId) && w.bottom > 0.3 &&
    supportKey(w, camera, keepSurface) && contains(w, camera.x, camera.y, radius)).sort((a, b) => a.bottom - b.bottom);
  // Never pass through a lower ceiling to reach a higher one.
  const surface = surfaces[0];
  if (!surface) return null;
  if (mountKind === 'ceiling-pendant' && Number.isFinite(lensHeight)) {
    mountDrop = surface.bottom - clearance - lensHeight;
    if (mountDrop < 0.1 - 1e-8 || mountDrop > 199) return null;
  }
  const h = surface.bottom - clearance - mountDrop;
  if (h < 0.2 || h > 60 || blocked(camera.x, camera.y, h, camera.x, camera.y, surface.bottom - 0.002, walls)) return null;
  return { ...camera, h, mountKind, mountDrop, mountSurface: supportKey(surface, camera, keepSurface) };
}

/** Placement settings are transient; saved cameras retain the physical drop. */
export function ceilingPlacement(store, point, model) {
  return ceilingCameraPose(store.doc, { ...point, mountDrop: point.mountDrop ?? store.newMountDrop ?? 0.3 }, model,
    { lensHeight: point.mountDrop == null && store.newSuspensionMode === 'height' ? store.newCeilingHeight ?? 3 : undefined });
}

export function findCeilingMount(S, camera, context) {
  if (!isCeilingCamera(S, camera)) return null;
  const model = S.models.find(m => m.id === camera.modelId);
  const pose = ceilingCameraPose(S, camera, model, { keepSurface: true, context });
  if (!pose || Math.abs(pose.h - camera.h) > 0.02) return null;
  return { x: camera.x, y: camera.y, height: pose.h + ceilingClearance(model) + pose.mountDrop, key: pose.mountSurface };
}

/** Structural edits carry attached cameras vertically, including cameras on
 * inactive levels. A deleted support is retained as an explicit audit issue. */
export function syncCeilingCameras(S) {
  const plans = [{ id: S.activeFloorId, plan: S, elevation: S.elevation || 0 }, ...(S.floors || [])];
  if (!plans.some(f => f.plan.cams.some(c => c.mountSurface && isCeilingCamera(f.plan, c)))) return;
  const context = buildingVisibility(S);
  for (const f of plans) for (const camera of f.plan.cams) {
    if (!camera.mountSurface || !isCeilingCamera(f.plan, camera)) continue;
    const pose = ceilingCameraPose({ ...f.plan, activeFloorId: f.id, elevation: f.elevation }, camera, undefined, { keepSurface: true, context });
    if (pose) camera.h = pose.h;
  }
}
